import { Fragment, useState, useRef } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClient } from '@/hooks/useAuth'
import { useUpdateAccentColor } from '@/hooks/useSettings'
import { usePlan } from '@/hooks/useConfig'
import { useProfile, useUpdatePrefs, useBusiness, useSetBusiness, type Business } from '@/hooks/useProfile'
import { SEED_COSTS, EMMA_FREE_PER_WEEK } from '@/hooks/useSeeds'
import { SeedPanel } from '@/components/settings/SeedPanel'
import { EMMA_COLORS } from '@/components/emma/emmaEngine'
import { useNudge } from '@/hooks/useNudge'
import { useNewsletterAllowed } from '@/hooks/useNewsletter'
import { useQueryClient } from '@tanstack/react-query'
import { ROUTES } from '@/components/gg/chrome/nav'
import { SettingsHome } from '@/pages/settings/SettingsHome'
import { useTopics } from '@/hooks/useConfig'
import { useClientPrefs, useUpdateClientPrefs } from '@/hooks/useMix'
import { VoiceInterview } from '@/components/onboarding/VoiceInterview'
import { VoiceProfileCard } from '@/components/voice/VoiceProfileCard'
import { useT } from '@/lib/i18n'
import '@/styles/voice.css'
import { PROFILES } from '@/components/onboarding/creatorProfiles'
import '@/styles/settings2.css'
import '@/styles/onboarding2.css'

/**
 * Settings is account and preferences only (Brian, Oct 22: "Settings to me is
 * like account and preferences. These would be feed controls"). With no tab it
 * is Brian's profile.html: the account, this week's seeds, the plan, the voice
 * per topic, and a row per setting with a live line (SettingsHome). A row
 * opens its detail here (?tab=…), with ‹ Settings to come back.
 */
export type SettingsTab = 'account' | 'plan' | 'seeds' | 'voice' | 'newsletter' | 'emma'
export const SETTINGS_TABS: { id: SettingsTab; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'plan', label: 'Plan' },
  { id: 'seeds', label: 'Seeds' },
  { id: 'voice', label: 'Voice' },
  { id: 'newsletter', label: 'Newsletter' },
  { id: 'emma', label: 'Emma' },
]
/** Old links: the feed's own controls moved out, appearance became Emma. */
const MOVED: Record<string, string> = { coverage: ROUTES.places, topics: ROUTES.topics }

export function settingsTab(raw: string | null): SettingsTab | null {
  if (raw === 'appearance') return 'emma'
  return SETTINGS_TABS.some((t) => t.id === raw) ? (raw as SettingsTab) : null
}

export function Settings() {
  const [params] = useSearchParams()
  const raw = params.get('tab') ?? ''
  if (MOVED[raw]) {
    const rest = new URLSearchParams(params); rest.delete('tab')
    const q = rest.toString()
    return <Navigate to={`${MOVED[raw]}${q ? `?${q}` : ''}`} replace />
  }
  const tab = settingsTab(raw)

  return (
    <div className="settings2">
      <div className="settings2__body">
        <div className="gg-screen__body settings2__scroll">
          {!tab ? <SettingsHome /> : (
            <div className="gg-column settings2__col">
              {tab === 'account' && <Account />}
              {tab === 'plan' && <PlanPanel />}
              {tab === 'seeds' && <SeedPanel />}
              {tab === 'voice' && <VoicePanel />}
              {tab === 'newsletter' && <NewsletterPanel />}
              {tab === 'emma' && <Appearance />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** The plan and what's in it (Brian's profile.html: price, seeds a week and about
 *  how many stories, places, topics, keywords, seats, the records budget and the
 *  alert budget). No change-plan or billing button yet: there is no screen. */
function PlanPanel() {
  const { data: plan } = usePlan()
  if (!plan) return <section className="gg-panel"><p className="gg-help">No plan on this account yet.</p></section>
  const rows: [string, string][] = [
    ['Seeds a week', `${plan.seeds_week} (about ${Math.floor(plan.seeds_week / SEED_COSTS.build_story)} stories)`],
    ['Places', String(plan.max_regions)],
    ['Topics', String(plan.max_topics)],
    ['Keywords', String(plan.max_pills ?? '')],
    ['Seats', String(plan.max_seats)],
    ['Alert budget', plan.alert_budget != null ? `Up to ${plan.alert_budget} seeds` : 'Not set'],
    ['Records budget', plan.records_budget != null ? `${plan.records_budget} sources checked every Monday` : 'Not set'],
    ['Newsletter', plan.newsletter_included ? 'Included' : 'Not included'],
    ['Emma', `${plan.emma_free_per_week ?? EMMA_FREE_PER_WEEK} questions a week included`],
  ]
  return (
    <section className="gg-panel">
      <h2 className="gg-panel__title">{plan.name}</h2>
      <p className="gg-help">${Math.round(plan.price_monthly / 100)} a month. Unused seeds roll over, up to a week’s worth.</p>
      <dl className="settings2__dl">
        {rows.map(([k, v]) => <Fragment key={k}><dt>{k}</dt><dd>{v}</dd></Fragment>)}
      </dl>
    </section>
  )
}

/** The voice profile (voice seeding v2): "Complete your voice profile, n of
 *  18 left" with Answer one (the ring on Emma), and the starter five from setup
 *  to answer again (onboarding's <VoiceInterview>; the answers stay saved). */
function InterviewSection() {
  const t = useT()
  const { data: topics } = useTopics()
  const [open, setOpen] = useState(false)
  const [done, setDone] = useState(false)
  const mix = (topics ?? []).filter((x) => (x.share ?? 0) > 0).sort((a, b) => (b.share ?? 0) - (a.share ?? 0)).map((x) => x.topic)
  if (open) {
    return (
      <section className="gg-panel settings2__interview">
        <VoiceInterview topics={mix} restart finishing={false} doneLabel={t('voice.done')}
          onBack={() => setOpen(false)}
          onFinish={(r) => { setOpen(false); if (r) setDone(true) }} />
      </section>
    )
  }
  return (
    <>
      <VoiceProfileCard />
      <section className="gg-panel">
        <h2 className="gg-panel__title">{t('settings.redoStarter')}</h2>
        <p className="gg-help">{t('settings.redoStarterLine')}</p>
        <button type="button" className="gg-btn gg-btn--sm" onClick={() => { setDone(false); setOpen(true) }} disabled={!topics}>
          {t('settings.redoStarter')}
        </button>
        {done && <p className="gg-ok">{t('settings.voiceSaved')}</p>}
      </section>
    </>
  )
}

/** Hyperlocal, Hybrid or Broad (clients.creator_profile). Brian has no screen
 *  for it after onboarding; it lives here, with the voice. */
function CreatorProfileSection() {
  const { data: prefs } = useClientPrefs()
  const update = useUpdateClientPrefs()
  const cur = prefs?.creator_profile ?? 'hybrid'
  return (
    <section className="gg-panel">
      <h2 className="gg-panel__title">What kind of creator you are</h2>
      <p className="gg-help">It shapes the topics Emma suggests. Your topic mix stays as it is.</p>
      <div className="settings2__profiles" role="radiogroup" aria-label="What kind of creator you are">
        {PROFILES.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={cur === p.id}
            className={`settings2__profile${cur === p.id ? ' is-on' : ''}`} disabled={!prefs || update.isPending}
            onClick={() => { if (cur !== p.id) update.mutate({ creator_profile: p.id }) }}>
            <strong>{p.label}</strong><span>{p.line}</span>
          </button>
        ))}
      </div>
    </section>
  )
}

/** The voice: the creator's standing sentence (AI-INPUTS rule 4), the voice
 *  interview and the creator profile. The position per topic lives with Topics
 *  in Feed controls. */
function VoicePanel() {
  const client = useEffectiveClient()
  const qc = useQueryClient()
  const ownClient = useAuthStore((s) => s.client)
  const setClient = useAuthStore((s) => s.setClient)
  const [draft, setDraft] = useState<string | null>(null)
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const value = draft ?? client?.voice_standing ?? ''
  return (
    <>
      <section className="gg-panel">
        <h2 className="gg-panel__title">Your standing</h2>
        <p className="gg-help">
          One sentence on who you are to your area, such as how long you have lived there or what you do.
          Scripts only make personal claims you state here, and it is never read aloud as a bio.
          Leave it empty and scripts make no personal claims at all.
        </p>
        <form className="settings2__add" onSubmit={async (e) => {
          e.preventDefault()
          if (!client) return
          setState('saving')
          const { error } = await supabase.from('clients').update({ voice_standing: value.trim() || null }).eq('id', client.id)
          setState(error ? 'error' : 'saved')
          if (!error) {
            // The account row lives in the auth store; keep it in step.
            if (ownClient?.id === client.id) setClient({ ...ownClient, voice_standing: value.trim() || null })
            setDraft(null); void qc.invalidateQueries({ queryKey: ['client-prefs'] })
          }
        }}>
          <textarea className="gg-textarea" value={value} maxLength={400} onChange={(e) => { setDraft(e.target.value); setState('idle') }}
            placeholder="I have lived in Bend for twelve years and run a coffee cart downtown." aria-label="Your standing sentence" />
          <button className="gg-btn" disabled={state === 'saving' || !client}>{state === 'saving' ? 'Saving' : 'Save'}</button>
        </form>
        {state === 'saved' && <p className="gg-ok">Saved.</p>}
        {state === 'error' && <p className="gg-error">Could not save your standing.</p>}
      </section>
      <section className="gg-panel">
        <h2 className="gg-panel__title">Position per topic</h2>
        <p className="gg-help">Newcomer, Reporter or Insider for each topic is set with the topic itself.</p>
        <Link className="gg-btn gg-btn--sm" to={ROUTES.topics}>Open Topics</Link>
      </section>
      <InterviewSection />
      <CreatorProfileSection />
    </>
  )
}

function NewsletterPanel() {
  const { data: allowed } = useNewsletterAllowed()
  return (
    <section className="gg-panel">
      <h2 className="gg-panel__title">Newsletter</h2>
      <p className="gg-help">
        {allowed
          ? 'Your newsletter is on. It is built best on a computer: this week’s issue, sponsors, your brand and your list.'
          : 'The newsletter is included on Grove and Harvest.'}
      </p>
      {allowed && (
        <div className="settings2__actions">
          <Link className="gg-btn gg-btn--sm" to={ROUTES.newsletter}>This week’s issue</Link>
          <Link className="gg-btn gg-btn--sm" to={ROUTES.newsletterConnect}>Your list</Link>
        </div>
      )}
    </section>
  )
}

function Appearance() {
  const client = useEffectiveClient()
  const updateAccent = useUpdateAccentColor()
  const { data: profile } = useProfile()
  const updatePrefs = useUpdatePrefs()
  const [accentDraft, setAccentDraft] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const accent = accentDraft ?? client?.accent_color ?? '#c8854a'
  const emmaColor = profile?.emma_color ?? 'amber'
  const skin = profile?.emma_skin ?? 'seed'

  function commit(v: string) {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    if (v !== client?.accent_color) updateAccent.mutate(v)
  }

  return (
    <>
      <section className="gg-panel">
        <h2 className="gg-panel__title">Emma</h2>
        <p className="gg-help">Her colour and her look. Every colour here keeps her eyes readable. The last swatch dresses her in your account&rsquo;s accent colour.</p>
        <div className="settings2__swatches" role="radiogroup" aria-label="Emma's colour">
          {EMMA_COLORS.map((c) => (
            <button key={c.id} role="radio" aria-checked={emmaColor === c.id} className={`settings2__swatch${emmaColor === c.id ? ' is-on' : ''}`}
              style={{ background: c.hex }} title={c.label} aria-label={c.label} onClick={() => updatePrefs.mutate({ emma_color: c.id })} />
          ))}
          {client?.accent_color && (
            <button role="radio" aria-checked={emmaColor === 'accent'} className={`settings2__swatch settings2__swatch--accent${emmaColor === 'accent' ? ' is-on' : ''}`}
              style={{ background: client.accent_color }} title="Your accent colour" aria-label="Your accent colour"
              onClick={() => updatePrefs.mutate({ emma_color: 'accent' })} />
          )}
        </div>
        <div className="gg-seg settings2__seg" role="group" aria-label="Emma's look">
          <button aria-pressed={skin === 'seed'} onClick={() => updatePrefs.mutate({ emma_skin: 'seed' })}>Seed</button>
          <button aria-pressed={skin === 'cowboy'} onClick={() => updatePrefs.mutate({ emma_skin: 'cowboy' })}>Cowboy</button>
        </div>
      </section>

      <section className="gg-panel">
        <h2 className="gg-panel__title">Accent colour</h2>
        <p className="gg-help">Used on your recordings and branded pieces.</p>
        <input type="color" className="settings2__color" value={accent} disabled={!client}
          onChange={(e) => { setAccentDraft(e.target.value); if (timer.current) clearTimeout(timer.current); const v = e.target.value; timer.current = setTimeout(() => commit(v), 400) }}
          onBlur={(e) => commit(e.target.value)} aria-label="Accent colour" />
        {updateAccent.isError && <p className="gg-error">Could not save the accent colour.</p>}
      </section>
    </>
  )
}

function Account() {
  const user = useAuthStore((s) => s.user)
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const client = useEffectiveClient()
  const nudge = useNudge()
  const { data: profile } = useProfile()
  const updatePrefs = useUpdatePrefs()
  const [handle, setHandle] = useState<string | null>(null)
  const [reset, setReset] = useState<{ kind: 'idle' | 'sending' | 'sent' | 'error'; message?: string }>({ kind: 'idle' })
  const handleValue = handle ?? profile?.display_handle ?? ''

  return (
    <>
      {nudge.supported && (
        <section className="gg-panel">
          <h2 className="gg-panel__title">Morning reminder</h2>
          <div className="settings2__line">
            <span className="gg-help">A daily nudge at {nudge.hour} in the morning, your time, when your headlines are ready.</span>
            <button role="switch" aria-checked={nudge.enabled} className="gg-switch" disabled={nudge.busy} onClick={() => { void nudge.toggle(!nudge.enabled) }} aria-label="Morning reminder" />
          </div>
          {nudge.blocked && <p className="gg-error">Notifications are off for GoGrov in your device settings.</p>}
        </section>
      )}

      <BusinessSection />

      <section className="gg-panel">
        <h2 className="gg-panel__title">Your name on a medal</h2>
        <p className="gg-help">Medal plates show your first name and initial, or a handle of up to 12 characters if you prefer.</p>
        <form className="settings2__add" onSubmit={(e) => { e.preventDefault(); updatePrefs.mutate({ display_handle: handleValue.trim() || null }) }}>
          <input className="gg-input" value={handleValue} maxLength={12} onChange={(e) => setHandle(e.target.value)} placeholder="Handle (optional)" aria-label="Medal handle" />
          <button className="gg-btn" disabled={updatePrefs.isPending}>Save</button>
        </form>
        {updatePrefs.isError && <p className="gg-error">{(updatePrefs.error as Error).message}</p>}
      </section>

      <section className="gg-panel">
        <h2 className="gg-panel__title">Account</h2>
        <dl className="settings2__dl">
          <dt>Email</dt><dd>{user?.email}</dd>
          <dt>Publication</dt><dd>{client?.display_name}</dd>
        </dl>
        <div className="settings2__actions">
          <button className="gg-btn" disabled={reset.kind === 'sending'} onClick={async () => {
            if (!user?.email) return
            setReset({ kind: 'sending' })
            const { error } = await supabase.auth.resetPasswordForEmail(user.email)
            setReset(error ? { kind: 'error', message: error.message } : { kind: 'sent', message: `Password reset email sent to ${user.email}.` })
          }}>{reset.kind === 'sending' ? 'Sending' : 'Reset password'}</button>
          {isAdmin && <Link className="gg-btn" to="/admin">Admin console</Link>}
          <Link className="gg-btn" to={ROUTES.profile}>Profile</Link>
          <Link className="gg-btn" to={ROUTES.affiliate}>Affiliate program</Link>
          <button className="gg-btn gg-btn--danger" onClick={() => { void supabase.auth.signOut() }}>Sign out</button>
        </div>
        {reset.message && <p className={reset.kind === 'error' ? 'gg-error' : 'gg-help'}>{reset.message}</p>}
      </section>
    </>
  )
}

/**
 * Creator or business (Brian's app-data.js PROFILES[...].kind and business;
 * profile.html: "a business account shows the line every post carries").
 * A business names its type, the line every post carries (a brokerage, a
 * licence, an NMLS number) and the check its posts run; every write carries
 * them and the caption ends with the line.
 */
function BusinessSection() {
  const { data: b } = useBusiness()
  const set = useSetBusiness()
  const [draft, setDraft] = useState<Business | null>(null)
  const v: Business = draft ?? b ?? { account_kind: 'creator', business_type: null, business_line: null, business_check: null }
  const patch = (p: Partial<Business>) => setDraft({ ...v, ...p })
  const isBiz = v.account_kind === 'business'
  return (
    <section className="gg-panel">
      <h2 className="gg-panel__title">Creator or business</h2>
      <p className="gg-help">A business account puts its line on every post and runs its check on every caption, such as fair housing for an agent or lending rules for a lender.</p>
      <div className="gg-seg settings2__seg" role="group" aria-label="Account kind">
        <button aria-pressed={!isBiz} onClick={() => patch({ account_kind: 'creator' })}>Creator</button>
        <button aria-pressed={isBiz} onClick={() => patch({ account_kind: 'business' })}>Business</button>
      </div>
      {isBiz && (
        <>
          <label className="gg-field"><span>What kind of business</span>
            <input className="gg-input" value={v.business_type ?? ''} maxLength={60} placeholder="Real estate"
              onChange={(e) => patch({ business_type: e.target.value })} />
          </label>
          <label className="gg-field"><span>The line on every post</span>
            <input className="gg-input" value={v.business_line ?? ''} maxLength={200} placeholder="Your name, Broker · Your brokerage · Licensed in Oregon"
              onChange={(e) => patch({ business_line: e.target.value })} />
          </label>
          <label className="gg-field"><span>The check your posts run</span>
            <input className="gg-input" value={v.business_check ?? ''} maxLength={200} placeholder="Fair housing and your state’s advertising rules"
              onChange={(e) => patch({ business_check: e.target.value })} />
          </label>
        </>
      )}
      {draft && (
        <div className="settings2__actions">
          <button className="gg-btn gg-btn--amber" disabled={set.isPending} onClick={() => set.mutate(v, { onSuccess: () => setDraft(null) })}>
            {set.isPending ? 'Saving' : 'Save'}
          </button>
        </div>
      )}
      {set.isError && <p className="gg-error">Could not save. Try again.</p>}
    </section>
  )
}
